import { type PublishedPayableRule } from '@spendesk/bookkeeping-core-types';
import type { MonetaryValue } from 'ezmoney';

export const earliestAllowedPayableDate = new Date('2010');
export const latestAllowedPayableDate = new Date('2060');

export type PayableId = string;

type CounterPartyUpdate = {
  id: string;
  type: 'employee' | 'supplier';
  name?: string;
  thumbnailUrl?: string;
};

type CustomFieldUpdate = {
  customFieldId: string;
  customFieldListValueId?: string;
  customFieldBooleanValue?: boolean;
};

type ItemLineUpdate = {
  taxAccountId: string | null;
  expenseAccountId: string | null;
  netAmount: MonetaryValue;
  grossAmount: MonetaryValue;
  vatAmount: MonetaryValue;
  vatAdjustment: MonetaryValue;
  analyticalFieldAssociations: {
    fieldEntityId: string;
    fieldEntityValueId: string;
    fieldKind: string;
  }[];
  vatRate: {
    amount: number;
    precision: number;
  };
  natureId: string;
};

export type PayableUpdate = {
  counterparty?: CounterPartyUpdate;
  description?: string;
  teamId?: string | null;
  costCenterId?: string | null;
  itemLines?: ItemLineUpdate[];
  customFieldAssociations?: CustomFieldUpdate[];
  accountPayableId?: string | null;
  invoiceNumber?: string;
  accountingDate?: string;
  creationDate?: string;
  amortisationDate?: PayableAmortisation['date'] | null;
  amortisationSchemeId?: string | null;
};

export type PayableAmortisation = {
  date: { from: string; to: string };
  schemeId?: string;
  schemeName?: string;
};

export type Member =
  | {
      type: 'user';
      id: string;
      givenName: string;
      familyName: string;
      email: string;
      avatar: string | undefined;
    }
  | {
      type: 'suspendedUser';
      id: string;
      givenName: string;
      familyName: string;
      avatar: string | undefined;
    }
  | {
      id: string;
      type: 'invitee';
      email: string;
    };

export type Supplier = {
  id: string;
  name: string;
  thumbnailUrl?: string | undefined;
};

type MileageDetails = {
  distance: string;
};

export type PerDiem = {
  duration: number;
};

export type AnalyticalFieldAssociation = {
  fieldKind: 'costCenter' | 'customField';
  fieldEntityId: string;
  fieldEntityValueId: string;
};

type PayableType =
  | 'single_purchase'
  | 'card_purchase'
  | 'subscription'
  | 'physical'
  | 'reversal'
  | 'expense_claim'
  | 'invoice_purchase'
  | 'mileage_allowance'
  | 'per_diem_allowance';

type PayableSubType =
  | 'plasticCard'
  | 'subscriptionCard'
  | 'multiUseCard'
  | 'singleUseCard'
  | 'creditNote';

/**
 * Automation
 */
export type MarvinConfidence = 'low' | 'medium' | 'high' | 'sure';

export type RuleOutput = {
  rule: PublishedPayableRule;
  output: string;
};

export type RuleBasedAutomation = {
  kind: 'ruleBased';
  isAppliedOnPayable: boolean;
  conflict?: [RuleOutput, RuleOutput, ...RuleOutput[]];
} & RuleOutput;

export function allRuleOutputsFromAutomation(
  automation?:
    | ExpenseAccountAutomation
    | VatAccountAutomation
    | AnalyticalFieldAssociationAutomation,
): RuleOutput[] {
  if (automation?.kind !== 'ruleBased') {
    return [];
  }
  return automation.conflict ?? [automation];
}

export type ExpenseAccountAutomation =
  | {
      kind: 'default';
      isAppliedOnPayable: boolean;
    }
  | {
      kind: 'prediction';
      expenseAccount: {
        id: string;
        name: string;
      };
      confidence: number;
      isAppliedOnPayable: boolean;
    }
  | {
      kind: 'expenseCategoryRule';
      isAppliedOnPayable: boolean;
    }
  | RuleBasedAutomation;

export type VatAccountAutomation = RuleBasedAutomation;

export type ExpenseAmountAutomation = {
  kind: 'recommendation';
  confidence?: MarvinConfidence;
  isAppliedOnPayable: boolean;
};

export type TaxAutomation =
  | {
      kind: 'default';
      isAppliedOnPayable: boolean;
    }
  | {
      kind: 'recommendation';
      confidence?: MarvinConfidence;
      isAppliedOnPayable: boolean;
    }
  | {
      kind: 'prediction';
      taxAccount: {
        id: string;
        name: string;
      };
      confidence?: MarvinConfidence;
      isAppliedOnPayable: boolean;
    };

export type AnalyticalFieldAssociationAutomation =
  | (AnalyticalFieldAssociation & {
      kind: 'prediction';
      isAppliedOnPayable: boolean;
    })
  | (Pick<
      AnalyticalFieldAssociation,
      'fieldKind' | 'fieldEntityId' | 'fieldEntityValueId'
    > &
      RuleBasedAutomation & { lineIndex: number });

export type DocumentaryEvidenceNumberAutomation = {
  kind: 'recommendation' | 'default';
  isAppliedOnPayable: boolean;
  documentaryEvidenceNumber?: string;
};

export type DocumentaryEvidenceDateAutomation = {
  kind: 'recommendation';
  isAppliedOnPayable: boolean;
  documentaryEvidenceDate?: Date;
};

export type PayableAutomation = {
  expenseAmount?: ExpenseAmountAutomation;
  expenseAccount?: ExpenseAccountAutomation;
  vatAccount?: VatAccountAutomation;
  accountPayable?: {
    accountPayableMatch: {
      id: string;
      generalAccountCode: string;
      isDefault: boolean;
      isArchived: boolean;
    };
    isAppliedOnPayable: boolean | null;
    isActive?: boolean;
  };
  tax?: TaxAutomation;
  analyticalFieldAssociations?: AnalyticalFieldAssociationAutomation[];
  documentaryEvidenceNumber?: DocumentaryEvidenceNumberAutomation;
  documentaryEvidenceDate?: DocumentaryEvidenceDateAutomation;
};

export function accountIdFromAutomation(
  automation?: ExpenseAccountAutomation | VatAccountAutomation,
) {
  return isPrefilledByAutomationRule(automation)
    ? automation.output
    : undefined;
}

export function isPrefilledByAutomationRule<
  T extends
    | ExpenseAccountAutomation
    | VatAccountAutomation
    | AnalyticalFieldAssociationAutomation,
>(automation?: T): automation is T & { kind: 'ruleBased' } {
  return Boolean(
    automation &&
      automation.isAppliedOnPayable &&
      automation.kind === 'ruleBased',
  );
}

export function isPrefilledByAutomationRulesOnly(
  automation?: PayableAutomation,
) {
  const hasExpenseAccountRule = isPrefilledByAutomationRule(
    automation?.expenseAccount,
  );
  const hasVatAccountRule = isPrefilledByAutomationRule(automation?.vatAccount);
  const hasAnalyticalRule = automation?.analyticalFieldAssociations?.some(
    isPrefilledByAutomationRule,
  );

  const hasSomeAutomationFromRules =
    hasExpenseAccountRule || hasVatAccountRule || hasAnalyticalRule;

  const hasSomeAutomationNotFromRules =
    (automation?.expenseAccount?.isAppliedOnPayable &&
      !hasExpenseAccountRule) ||
    automation?.tax?.isAppliedOnPayable ||
    automation?.analyticalFieldAssociations?.some(
      (afaAutomation) =>
        afaAutomation.isAppliedOnPayable &&
        !isPrefilledByAutomationRule(afaAutomation),
    );

  return Boolean(hasSomeAutomationFromRules && !hasSomeAutomationNotFromRules);
}

/**
 * Payable
 */

export type Payable = {
  id: string;
  version: number;
  kind: 'expense' | 'reversal';
  subtype: PayableSubType | undefined;
  type: PayableType;
  description: string;
  counterparty: Member | (Supplier & { type: 'supplier' }) | undefined;
  member: Member | undefined;
  supplier: Supplier | undefined;
  documentaryEvidence:
    | {
        type: 'invoice' | 'declarationOfMissingInvoice';
        invoiceNumber: string | undefined;
      }
    | { type: 'creditNote'; creditNoteNumber: string | undefined }
    | undefined;
  grossAmount: MonetaryValue;
  functionalAmount: MonetaryValue;
  mileageDetails: MileageDetails | undefined;
  perDiem: PerDiem | undefined;
  costCenter?: { name: string } | null;
  creationDate: Date;
};
